import React, { useState, useEffect } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Home from '../../pages/Home';
import Issues from '../../pages/Issues';
import InspirationMachine from '../../pages/InspirationMachine';
import InspirationNationColumn from '../../pages/InspirationNationColumn';
import Interviews from '../../pages/Interviews';
import PromptGenerator from '../../pages/PromptGenerator';
import InspirationGallery from '../../pages/InspirationGallery';
import NotFound from '../../pages/NotFound';
import Issue from '../../pages/Page/Issue';
import Interview from '../../pages/Page/Interview';
import InspirationPost from '../../pages/Page/InspirationPost';
import Loading from '../../components/Loading';
import NavigationContext from '../../context/navigationContext';

import { useApiActions } from '../../hooks';
import styles from './routes.css';

const Routes = ({ location }) => {
  const {
    getNavLinks,
    getFooterLinks,
    getHomepage,
    getIssues,
    getInterviews,
    getPostsByCategory,
  } = useApiActions();

  const [loadProgress, setLoadProgress] = useState(0);
  const [navLinks, setNavLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [homepage, setHomepage] = useState({ introduction: '', resources: [] });
  const [issues, setIssues] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [inspirationPosts, setInspirationPosts] = useState([]);

  const INSPIRATION_NATION_CATEGORY_ID = 11;

  useEffect(() => {
    Promise.all([
      getNavLinks(),
      getFooterLinks(),
      getHomepage(),
      getIssues(),
      getInterviews(),
      getPostsByCategory(INSPIRATION_NATION_CATEGORY_ID, 'inspiration-nation'),
    ])
      .then(([navLinks, footerLinks, homepage, issues, interviews, inspirationPosts]) => {
        setNavLinks(navLinks);
        setFooterLinks(footerLinks);
        setHomepage(homepage);
        setIssues(issues);
        setInterviews(interviews);
        setInspirationPosts(inspirationPosts);
      })
      .finally(() => {
        setLoadProgress(100);
        // Clear loading screen after 0.5s delay
        setTimeout(() => setLoadProgress(101), 500);
      });
  }, []);

  const getRecircPost = (posts, index) => {
    const recircPostIdx = index === posts.length - 1 ? 0 : index + 1;
    return posts[recircPostIdx];
  };

  return (
    <NavigationContext.Provider value={{ navLinks, footerLinks }}>
      <div className={`${styles.wrapper} ${isMobile || isTablet ? styles.touch : ''}`}>
        <TransitionGroup className={styles.transitionGroup}>
          <CSSTransition
            key={location.key}
            timeout={{ enter: 200, exit: 200 }}
            classNames={{
              enter: styles.fadeEnter,
              enterActive: styles.fadeEnterActive,
              exit: styles.fadeExit,
              exitActive: styles.fadeExitActive,
            }}>
            <section className={styles.routeSection}>
              {loadProgress <= 100 ? (
                <Loading progress={loadProgress} />
              ) : (
                <Switch location={location}>
                  <Route
                    path="/"
                    render={() => <Home homepage={homepage} interviews={interviews} />}
                    exact
                  />
                  <Route path="/issues" render={() => <Issues issues={issues} />} />
                  {issues.map((page, index) => (
                    <Route
                      key={page.path}
                      path={page.path}
                      render={() => (
                        <Issue {...page} recircPost={getRecircPost(issues, index)} />
                      )}
                    />
                  ))}
                  <Route
                    path="/interviews"
                    render={({ match: { url } }) => (
                      <>
                        <Route
                          path={`${url}/`}
                          render={() => <Interviews interviews={interviews} />}
                          exact
                        />
                        {interviews.map((page, index) => (
                          <Route
                            key={page.path}
                            path={page.path}
                            render={() => (
                              <Interview {...page} recircPost={getRecircPost(interviews, index)} />
                            )}
                          />
                        ))}
                      </>
                    )}
                  />
                  <Route
                    path="/inspiration-nation"
                    render={({ match: { url } }) => (
                      <>
                        <Route
                          path={`${url}/`}
                          render={() => <InspirationNationColumn posts={inspirationPosts} />}
                          exact
                        />
                        {inspirationPosts.map((post, index) => (
                          <Route
                            key={post.path}
                            path={post.path}
                            render={() => (
                              <InspirationPost
                                {...post}
                                recircPost={getRecircPost(inspirationPosts, index)}
                              />
                            )}
                          />
                        ))}
                      </>
                    )}
                  />
                  <Route path="/inspiration-machine" render={() => <InspirationMachine />} />
                  <Route path="/design-prompt-generator" render={() => <PromptGenerator />} />
                  <Route path="/inspiration-gallery" render={() => <InspirationGallery />} />
                  <Route render={() => <NotFound />} />
                </Switch>
              )}
            </section>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </NavigationContext.Provider>
  );
};

Routes.propTypes = {
  location: PropTypes.object.isRequired,
};

export default withRouter(Routes);
